import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import TimeHelper from '../../../utils/time';
import { downloadExcel } from '../../../utils/excelFileHelper';
import AddEditDrawer from './AddEditDrawer';
import { COLT } from '../cutOff.classes';

interface ListTabProps {
    setIsLoading?: (loading: boolean) => void;
    cargoCompanyOptions?: { id: number; name: string }[];
    gridData?: any[];
    refreshGridData?: () => void;
}

export const ListTab = ({ setIsLoading, cargoCompanyOptions = [], gridData = [], refreshGridData }: ListTabProps) => {
    const { t } = useTranslation();
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [isAddEditDrawerOpen, setIsAddEditDrawerOpen] = useState<boolean>(false);
    const [dataToEdit, setDataToEdit] = useState<any>(null);

    const handleEdit = (data: any) => {
        setDataToEdit(data);
        setIsAddEditDrawerOpen(true);
    };

    const handleAddNew = () => {
        setDataToEdit(null);
        setIsAddEditDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsAddEditDrawerOpen(false);
        setDataToEdit(null);
    };

    const handleSave = () => {
        handleDrawerClose();
        // Refresh grid data after save
        refreshGridData?.();
    };

    const handleExportExcel = () => {
        const excelRows = (gridData ?? []).map((row: any) => ({
            cargoCompanyName: row?.shippingCompanyName ?? '-',
            shippingCompanyId: row?.shippingCompanyId ?? '-',
            cutOffTime: row?.cutoffTime ?? '-',
            departureLocationName: row?.departureLocationName ?? '-',
            departureLocationId: row?.departureLocationId ?? '-',
            arrivalLocationName: row?.arrivalLocationName ?? '-',
            arrivalLocationId: row?.arrivalLocationId ?? '-',
            distanceRangeName: row?.distanceRangeName ?? '-',
            distanceRangeId: row?.distanceRangeId ?? '-',
            deliveryType: row?.deliveryType ?? '-',
            deliveryTypeId: row?.deliveryTypeId ?? '-',
            activeStatus: typeof row?.isActive === 'boolean' ? String(row.isActive) : '-',
            commitmentDay: row?.commitmentDay ?? '-',
            startDate: row?.validFromDate ? TimeHelper.formatDate(row.validFromDate) : '-',
            endDate: row?.validToDate ? TimeHelper.formatDate(row.validToDate) : '-',
            updatedBy: row?.changedByName ?? '-',
            updateDate: row?.changeOn ? TimeHelper.formatDate(row.changeOn) : '-',
            addressTypeId: row?.addressTypeId ?? '-',
        }));

        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `CutOff_${dd}.${mm}.${yyyy}.xlsx`;

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: 'CutOff',
            fileNameOverride: fileName,
        });
    };

    const columnData: any = [
        {
            field: 'shippingCompanyName',
            headerName: t('cutOff.cargoCompany'),
            flex: 1,
            minWidth: 150,
            renderCell: (params: any) => <span>{params?.row?.shippingCompanyName ?? '-'}</span>,
        },
        {
            field: 'cutoffTime',
            headerName: t('cutOff.cutOffTime'),
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => <span>{params?.row?.cutoffTime ?? '-'}</span>,
        },
        {
            field: 'departureLocationName',
            headerName: t('cutOff.departureLocation'),
            flex: 1,
            minWidth: 120,
            renderCell: (params: any) => <span>{params?.row?.departureLocationName ?? '-'}</span>,
        },
        {
            field: 'arrivalLocationName',
            headerName: t('cutOff.arrivalLocation'),
            flex: 1,
            minWidth: 120,
            renderCell: (params: any) => <span>{params?.row?.arrivalLocationName ?? '-'}</span>,
        },
        {
            field: 'distanceRangeName',
            headerName: t('cutOff.distance'),
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => <span>{params?.row?.distanceRangeName ?? '-'}</span>,
        },
        {
            field: 'deliveryType',
            headerName: t('cutOff.deliveryType'),
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => <span>{params?.row?.deliveryType ?? '-'}</span>,
        },
        {
            field: 'isActive',
            headerName: t('cutOff.activeStatus'),
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const isActive = params?.row?.isActive;
                return (
                    <Chip
                        label={isActive ? t('general.active') : t('general.passive')}
                        size="small"
                        sx={{
                            backgroundColor: isActive ? '#4caf50' : '#f44336',
                            color: 'white',
                            fontWeight: 500,
                        }}
                    />
                );
            },
        },
        {
            field: 'commitmentDay',
            headerName: t('cutOff.commitmentDay'),
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const day = params?.row?.commitmentDay;
                return <span>{day != null ? `${day} ${t('general.day')}` : '-'}</span>;
            },
        },
        {
            field: 'validFromDate',
            headerName: t('cutOff.startDate'),
            width: 160,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                if (params?.row?.validFromDate) {
                    return <span>{TimeHelper.formatDate(params.row.validFromDate)}</span>;
                }
                return <span>-</span>;
            },
        },
        {
            field: 'validToDate',
            headerName: t('cutOff.endDate'),
            width: 160,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                if (params?.row?.validToDate) {
                    return <span>{TimeHelper.formatDate(params.row.validToDate)}</span>;
                }
                return <span>-</span>;
            },
        },
        {
            field: 'changedByName',
            headerName: t('cutOff.updatedBy'),
            flex: 1,
            minWidth: 180,
            renderCell: (params: any) => <span>{params?.row?.changedByName ?? '-'}</span>,
        },
        {
            field: 'changeOn',
            headerName: t('cutOff.updatedDate'),
            width: 160,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                if (params?.row?.changeOn) {
                    return <span>{TimeHelper.formatDate(params.row.changeOn)}</span>;
                }
                return <span>-</span>;
            },
        },
        {
            field: 'actions',
            headerName: t('general.detail'),
            width: 80,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (cellData: any) => {
                return (
                    <PopupState variant="popover" popupId="cut-off-list-menu">
                        {(popupState: any) => (
                            <>
                                <IconButton className={COLT.inputIconRowActions} {...bindTrigger(popupState)}>
                                    <MoreVertIcon className="svg_icons" />
                                </IconButton>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem
                                        onClick={() => {
                                            handleEdit(cellData?.row);
                                            popupState.close();
                                        }}
                                    >
                                        {t('general.edit')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </PopupState>
                );
            },
        },
    ];

    return (
        <Box className={COLT.root} sx={{ width: '100%' }}>
            {/* Action Buttons */}
            <Box className={COLT.toolbar} sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                <SCUIButton
                    className={COLT.inputButtonAddNew}
                    text={t('general.addNew')}
                    type="primary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleAddNew}
                    showStartIcon
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                />
                <SCUIButton
                    className={COLT.inputButtonExportExcel}
                    text={t('general.exportToExcel')}
                    type="secondary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleExportExcel}
                    showStartIcon
                    startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                />
            </Box>

            <Box className={COLT.datagridWrap}>
            <SCUICustomDataGrid
                onPageSizeChange={(i: number) => {
                    setPageSize(i);
                }}
                pageSize={pageSize}
                onPageChange={(i: number) => {
                    setPageNumber(i);
                }}
                getRowId={(row: any) => row?.id}
                page={pageNumber}
                rows={gridData ?? []}
                columns={columnData}
            />
            </Box>

            <AddEditDrawer
                isDrawerOpen={isAddEditDrawerOpen}
                handleDrawerClose={handleDrawerClose}
                editData={dataToEdit}
                cargoCompanyOptions={cargoCompanyOptions}
                onSave={handleSave}
                setIsLoading={setIsLoading}
            />
        </Box>
    );
};
